/** Nachrichtenaktionen ohne UI (REST + sofortige Übernahme in den Store). */
import type { ChannelDto, MessageDto } from '@lagerfeuer/shared';
import { messagesApi } from '../../lib/endpoints';
import { pathForChannel } from '../../lib/routes';
import { appState } from '../../store/app';
import { toast } from '../../store/ui';
import { describeSendError } from './send';
import { create } from 'zustand';

export async function deleteMessage(m: Pick<MessageDto, 'id' | 'channelId' | 'serverId'>): Promise<boolean> {
  try {
    await messagesApi.remove(m.channelId, m.id);
    appState().dispatch('MESSAGE_DELETE', { id: m.id, channelId: m.channelId, serverId: m.serverId });
    return true;
  } catch (e) {
    toast.error('Löschen fehlgeschlagen', describeSendError(e));
    return false;
  }
}

export async function editMessage(m: Pick<MessageDto, 'id' | 'channelId'>, content: string): Promise<boolean> {
  try {
    const updated = await messagesApi.edit(m.channelId, m.id, { content });
    appState().dispatch('MESSAGE_UPDATE', updated);
    return true;
  } catch (e) {
    toast.error('Bearbeiten fehlgeschlagen', describeSendError(e));
    return false;
  }
}

export async function setPinned(m: Pick<MessageDto, 'id' | 'channelId'>, pinned: boolean): Promise<boolean> {
  try {
    if (pinned) await messagesApi.pin(m.channelId, m.id);
    else await messagesApi.unpin(m.channelId, m.id);
    appState().update((d) => {
      const msg = d.messages[m.channelId]?.items.find((x) => x.id === m.id);
      if (msg) msg.pinned = pinned;
    });
    toast.success(pinned ? 'Nachricht angeheftet' : 'Nachricht gelöst');
    return true;
  } catch (e) {
    toast.error(pinned ? 'Anheften fehlgeschlagen' : 'Lösen fehlgeschlagen', describeSendError(e));
    return false;
  }
}

export async function copyText(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} kopiert`);
  } catch {
    toast.error('Kopieren nicht möglich', 'Die Zwischenablage ist nicht verfügbar.');
  }
}

export function messageLink(channel: Pick<ChannelDto, 'id' | 'serverId' | 'type' | 'parentId'>, messageId: string): string {
  return `${window.location.origin}${pathForChannel(channel, messageId)}`;
}

/** Dialoge des Nachrichtenbereichs (Löschen bestätigen, Thread erstellen). */
export type MessageDialog = { kind: 'delete'; message: MessageDto } | { kind: 'thread'; message: MessageDto };

export const useMessageDialog = create<{ dialog: MessageDialog | null; open(d: MessageDialog): void; close(): void }>((set) => ({
  dialog: null,
  open: (dialog) => set({ dialog }),
  close: () => set({ dialog: null }),
}));
